import { User } from '@mi-front/core';
import { useQuery } from '@tanstack/react-query';
import { QueryView } from '@ui/utils/async-view/QueryView';
import { getAppUserDisplayName } from '../../helpers/user';
import { fetchUser } from '../../modules/admin/api';
import { UserIcon } from './UserIcon';

interface UserDisplayProps {
  id?: string;
  user?: Partial<Pick<User, 'name' | 'surname' | 'email'>>;
  showIcon?: boolean;
}

/** Wraps content with optional small user icon when showIcon is true. */
function wrapWithIcon(content: React.ReactNode, showIcon?: boolean) {
  if (showIcon) {
    return (
      <>
        <UserIcon sx={{ fontSize: 16, mt: '-3px', mr: 0.5, color: 'text.secondary' }} />
        {content}
      </>
    );
  }
  return <>{content}</>;
}

export function UserLabel({ id, user, showIcon }: UserDisplayProps) {
  const userQuery = useQuery({
    queryKey: ['user', id],
    queryFn: () => fetchUser(id!),
    staleTime: Infinity,
    enabled: !!id,
  });

  // If user object is provided, use it directly
  if (user) {
    return wrapWithIcon(getAppUserDisplayName(user as User, 'long'), showIcon);
  }

  // If only ID is provided, fetch user data
  if (id) {
    return (
      <QueryView inline query={userQuery}>
        {(data) => wrapWithIcon(getAppUserDisplayName(data, 'long'), showIcon)}
      </QueryView>
    );
  }

  return null;
}
